import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import {
  isArchivedAssignment,
  getArchivedAssignmentLabel,
  formatUnassignedDate,
} from '../../utils/assignmentHistory';
import {
  LuArrowDown,
  LuArrowUp,
  LuArrowUpDown,
  LuBuilding2,
  LuFileSpreadsheet,
  LuFolderKanban,
  LuRefreshCw,
  LuSheet,
  LuStethoscope,
  LuUsers,
  LuX,
} from 'react-icons/lu';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { SelectInput } from '../../components/form/FormControls';
import { useTranslation } from '../../i18n/LanguageContext';
import { EmptyState, LoadingRows, SearchField } from '../ProfilePage/components/ProfileUi';
import CollapsibleCard from './operations/CollapsibleCard';

// SC / Durum / Telefon / E-Posta kişiye ait alanlar; bir SC'nin bütün
// satırlarında aynı değeri tekrar ederler. Bu alanlardan birine göre
// sıralandığında aynı kişinin satırları zaten yan yana geldiği için satırları
// gruplayıp bu dört sütunu tek bir birleşik hücrede gösteriyoruz. Başka bir
// sütuna göre sıralandığında gruplama anlamını yitirir ve düz listeye dönülür.
const PERSON_LEVEL_FIELDS = ['scName', 'employeeStatus', 'phone', 'email'];

const initialsOf = (name) =>
  String(name || '')
    .split(' ')
    .map((part) => part[0])
    .join('')
    .substring(0, 2);

const CELL = 'tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-align-top tw-text-sm tw-text-slate-700 dark:tw-text-slate-200';
const NUM_CELL = `${CELL} tw-text-right tw-tabular-nums`;
const MERGED = 'tw-bg-slate-50/70 dark:tw-bg-white/[0.02]';

/** Kişiye ait sütunlar. rowSpan verildiğinde grubun tamamına yayılır. */
const IdentityCells = ({ row, rowSpan, groupSize }) => {
  const { t } = useTranslation();
  const merged = rowSpan ? MERGED : '';
  return (
    <>
      <td className={`${CELL} ${merged}`} rowSpan={rowSpan}>
        <span className="tw-flex tw-items-center tw-gap-2.5">
          <span aria-hidden="true" className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-[11px] tw-font-semibold tw-text-white">
            {initialsOf(row.scName)}
          </span>
          <span className="tw-min-w-0">
            <span className="tw-block tw-font-semibold tw-text-slate-900 dark:tw-text-white">{row.scName}</span>
            {groupSize > 1 && (
              <span className="tw-block tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{t('operations.sc.groupCount', { count: groupSize })}</span>
            )}
          </span>
        </span>
      </td>
      <td className={`${CELL} ${merged}`} rowSpan={rowSpan}>
        <Badge tone={row.employeeStatus === 'active' ? 'success' : 'neutral'}>
          {row.employeeStatus === 'active' ? t('operations.sc.active') : t('operations.sc.inactive')}
        </Badge>
      </td>
      <td className={`${CELL} tw-tabular-nums ${merged}`} rowSpan={rowSpan}>
        {row.phone}
      </td>
      <td className={`${CELL} ${merged}`} rowSpan={rowSpan}>
        {row.email}
      </td>
    </>
  );
};

IdentityCells.propTypes = { row: PropTypes.object.isRequired, rowSpan: PropTypes.number, groupSize: PropTypes.number };

const FTE_TONES = { Backup: 'warning', 1: 'success' };

/** Göreve (proje + merkez) ait sütunlar; her satırda ayrı gösterilir. */
const AssignmentCells = ({ row }) => (
  <>
    <td className={CELL}>
      <Badge tone={FTE_TONES[row.serviceFte] || 'info'}>{row.serviceFte || '-'}</Badge>
    </td>
    <td className={`${CELL} tw-font-semibold tw-text-slate-900 dark:tw-text-white`}>{row.project}</td>
    <td className={CELL}>{row.protocolNumber}</td>
    <td className={CELL}>
      {row.indication && <Badge tone="accent">{row.indication}</Badge>}
    </td>
    <td className={`${CELL} tw-max-w-[16rem] tw-whitespace-normal`} title={row.site}>
      {row.site}
      {row.isArchived && (
        <span className="tw-mt-1 tw-block">
          <Badge tone="neutral">{getArchivedAssignmentLabel(row)}</Badge>
        </span>
      )}
    </td>
    <td className={`${CELL} tw-max-w-[14rem] tw-truncate`} title={row.pi}>
      {row.pi}
    </td>
    <td className={`${CELL} tw-tabular-nums`}>{row.delegation}</td>
    <td className={NUM_CELL}>{row.targetedPatients || '-'}</td>
    <td className={`${NUM_CELL} tw-font-semibold tw-text-sky-700 dark:tw-text-sky-300`}>{row.screenedPatients || '-'}</td>
    <td className={`${NUM_CELL} tw-font-semibold tw-text-rose-600 dark:tw-text-rose-300`}>{row.screenFailure || '-'}</td>
    <td className={`${NUM_CELL} tw-font-semibold tw-text-violet-700 dark:tw-text-violet-300`}>{row.randomization || '-'}</td>
    <td className={`${NUM_CELL} tw-font-semibold tw-text-emerald-700 dark:tw-text-emerald-300`}>{row.currentPatients || '-'}</td>
    <td className={CELL}>{row.sponsor}</td>
  </>
);

AssignmentCells.propTypes = { row: PropTypes.object.isRequired };

const SCTrackingTable = () => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`operations.sc.${key}`, vars);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Raw data
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [centers, setCenters] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [externalPersonnels, setExternalPersonnels] = useState([]);

  // Table state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('scName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'sc', 'backup'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'

  /**
   * Fetch all required data from Firebase
   */
  const fetchAllData = useCallback(async (forceRefresh = false) => {
    if (dataLoaded && !forceRefresh) return;
    setIsLoading(true);
    try {
      const [
        employeesSnap,
        projectsSnap,
        centersSnap,
        customersSnap,
        patientsSnap,
        externalSnap
      ] = await Promise.all([
        getDocs(collection(db, 'Employees')),
        getDocs(collection(db, 'Projects')),
        getDocs(collection(db, 'Centers')),
        getDocs(collection(db, 'Customers')),
        getDocs(collection(db, 'Patients')),
        getDocs(collection(db, 'ExternalPersonnels'))
      ]);

      setEmployees(employeesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setProjects(projectsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setCenters(centersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setCustomers(customersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setPatients(patientsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setExternalPersonnels(externalSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      setDataLoaded(true);
      toast.success(label('toast.loaded'));
    } catch (error) {
      console.error('Error fetching SC tracking data:', error);
      toast.error(label('toast.loadError'));
    } finally {
      setIsLoading(false);
    }
    // label yalnızca dil değişince değişir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded]);

  // Load data when expanded
  useEffect(() => {
    if (isExpanded && !dataLoaded) {
      fetchAllData();
    }
  }, [isExpanded, dataLoaded, fetchAllData]);

  // Handle manual refresh
  const handleRefresh = () => {
    fetchAllData(true);
  };

  // Handle search input change with error handling
  const handleSearchChange = useCallback((e) => {
    try {
      const value = e.target.value || '';
      setSearchTerm(value);
    } catch (error) {
      console.error('Search input error:', error);
      toast.error(label('toast.searchError'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
    setSortField('scName');
    setSortDirection('asc');
  };

  // Lookup maps
  const employeeMap = useMemo(() => {
    const map = new Map();
    employees.forEach(emp => map.set(emp.id, emp));
    return map;
  }, [employees]);

  const projectMap = useMemo(() => {
    const map = new Map();
    projects.forEach(proj => map.set(proj.id, proj));
    return map;
  }, [projects]);

  const centerMap = useMemo(() => {
    const map = new Map();
    centers.forEach(c => map.set(c.id, c));
    return map;
  }, [centers]);

  const customerMap = useMemo(() => {
    const map = new Map();
    customers.forEach(c => map.set(c['Başlık'], c));
    return map;
  }, [customers]);

  // Build patient stats grouped by projectId
  const patientStatsByProject = useMemo(() => {
    const statsMap = new Map(); // key: projectId => stats
    patients.forEach(p => {
      const key = p.projectId;
      if (!key) return;
      if (!statsMap.has(key)) {
        statsMap.set(key, {
          targeted: 0,
          screened: 0,
          screenFail: 0,
          randomized: 0,
          currentActive: 0,
          total: 0
        });
      }
      const stat = statsMap.get(key);
      stat.total++;
      if (p.status === 'screened' || p.status === 'screenFail' || p.status === 'enrolled' || 
          p.status === 'active' || p.status === 'onMedication' || p.status === 'completed' || p.status === 'withdrawn') {
        stat.screened++;
      }
      if (p.status === 'screenFail') {
        stat.screenFail++;
      }
      if (p.status === 'enrolled' || p.status === 'active' || p.status === 'onMedication' || p.status === 'completed') {
        stat.randomized++;
      }
      if (p.status === 'active' || p.status === 'onMedication') {
        stat.currentActive++;
      }
    });
    return statsMap;
  }, [patients]);

  // Build patient stats grouped by projectId AND centerId (for per-center tracking)
  const patientStatsByProjectCenter = useMemo(() => {
    const statsMap = new Map(); // key: `${projectId}_${centerId}` => stats
    patients.forEach(p => {
      const projectId = p.projectId;
      const centerId = p.centerId || 'unknown';
      if (!projectId) return;
      const key = `${projectId}_${centerId}`;
      if (!statsMap.has(key)) {
        statsMap.set(key, {
          targeted: 0,
          screened: 0,
          screenFail: 0,
          randomized: 0,
          currentActive: 0,
          total: 0
        });
      }
      const stat = statsMap.get(key);
      stat.total++;
      if (['screened', 'screenFail', 'enrolled', 'active', 'onMedication', 'completed', 'withdrawn'].includes(p.status)) {
        stat.screened++;
      }
      if (p.status === 'screenFail') stat.screenFail++;
      if (['enrolled', 'active', 'onMedication', 'completed'].includes(p.status)) stat.randomized++;
      if (p.status === 'active' || p.status === 'onMedication') stat.currentActive++;
    });
    return statsMap;
  }, [patients]);

  /**
   * Build rows: one row per employee-project-center assignment
   * This matches the Excel structure where each SC has multiple rows (one per project/center)
   */
  const tableRows = useMemo(() => {
    const rows = [];

    projects.forEach(project => {
      const projectEmployees = project.employees || [];
      
      projectEmployees.forEach(assignment => {
        // Only include SC-related roles
        const role = assignment.role || '';
        const isSC = role === 'Saha Koordinatörü';
        const isBackup = role === 'Backup Personeli';
        const isNurse = role === 'Klinik Araştırma Hemşiresi';
        const isSCSorumlusu = role === 'Saha Koordinatörü Sorumlusu';
        
        if (!isSC && !isBackup && !isNurse && !isSCSorumlusu) return;

        const employee = employeeMap.get(assignment.id);
        if (!employee) return;
        // Include both active and inactive employees for comprehensive tracking
        // Skip only if employee data is completely missing

        const center = assignment.centerId ? centerMap.get(assignment.centerId) : null;
        const customerName = project.customer || '';
        
        // Get FTE/tariff info
        const rate = assignment.rate;
        let fteDisplay = '';
        if (rate) {
          if (rate.type?.toUpperCase() === 'FTE') {
            fteDisplay = rate.value || '';
          } else {
            fteDisplay = `Saatlik-${rate.value || ''}`;
          }
        }
        if (isBackup) {
          fteDisplay = 'Backup';
        }

        // Get patient stats for this project (or project+center)
        const projectPatientStats = patientStatsByProject.get(project.id) || {
          targeted: 0, screened: 0, screenFail: 0, randomized: 0, currentActive: 0, total: 0
        };

        // Delegation date
        let delegationDate = '';
        if (assignment.assignedAt) {
          try {
            const d = assignment.assignedAt.toDate ? assignment.assignedAt.toDate() : new Date(assignment.assignedAt);
            delegationDate = d.toLocaleDateString('tr-TR');
          } catch {
            delegationDate = String(assignment.assignedAt);
          }
        }

        // Find PI (principal investigator) from ExternalPersonnels linked via center
        let piName = '';
        if (center && center['Yetkili']) {
          const pi = externalPersonnels.find(ep => ep.id === center['Yetkili']);
          if (pi) {
            piName = `${pi['Adı'] || ''} ${pi['Soyadı'] || ''}`.trim();
            if (pi['Ünvan']) {
              piName = `${pi['Ünvan']} ${piName}`;
            }
          }
        }

        rows.push({
          scName: `${employee.eName || ''} ${employee.eSurname || ''}`.trim() || 'Bilinmeyen',
          phone: employee.phone || '',
          email: employee.eMail || '',
          serviceFte: fteDisplay || '',
          project: project.titleShort || project.titleLong || '',
          protocolNumber: project.protocolNumber || '',
          indication: project.projectDepartment || '',
          site: center ? (center['Adı'] || '') : (assignment.centerName || ''),
          pi: piName || '',
          delegation: delegationDate || '',
          targetedPatients: projectPatientStats.total || 0,
          screenedPatients: projectPatientStats.screened || 0,
          screenFailure: projectPatientStats.screenFail || 0,
          randomization: projectPatientStats.randomized || 0,
          currentPatients: projectPatientStats.currentActive || 0,
          sponsor: customerName || '',
          role: role || '',
          status: assignment.status || 'active',
          employeeStatus: employee.Active === 1 ? 'active' : 'inactive',
          employeeId: employee.id || '',
          projectId: project.id || '',
          centerId: assignment.centerId || '',
          // Bu merkezden ayrılmış olsa da satır listede kalır, rozetle işaretlenir.
          isArchived: isArchivedAssignment(assignment),
          unassignedAt: assignment.unassignedAt || ''
        });
      });
    });

    return rows;
  }, [projects, employeeMap, centerMap, patientStatsByProject, externalPersonnels]);

  // Filter & sort
  const filteredAndSortedRows = useMemo(() => {
    try {
      let filtered = [...tableRows];

      // Role filter
      if (roleFilter === 'sc') {
        filtered = filtered.filter(r => r.role === 'Saha Koordinatörü');
      } else if (roleFilter === 'backup') {
        filtered = filtered.filter(r => r.role === 'Backup Personeli');
      } else if (roleFilter === 'nurse') {
        filtered = filtered.filter(r => r.role === 'Klinik Araştırma Hemşiresi');
      } else if (roleFilter === 'scSorumlusu') {
        filtered = filtered.filter(r => r.role === 'Saha Koordinatörü Sorumlusu');
      }

      // Status filter
      if (statusFilter === 'active') {
        filtered = filtered.filter(r => r.employeeStatus === 'active');
      } else if (statusFilter === 'inactive') {
        filtered = filtered.filter(r => r.employeeStatus === 'inactive');
      }

      // Search filter
      if (searchTerm.trim()) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(row => {
          try {
            return (
              (row.scName || '').toLowerCase().includes(search) ||
              (row.project || '').toLowerCase().includes(search) ||
              (row.site || '').toLowerCase().includes(search) ||
              (row.email || '').toLowerCase().includes(search) ||
              (row.pi || '').toLowerCase().includes(search) ||
              (row.sponsor || '').toLowerCase().includes(search) ||
              (row.protocolNumber || '').toLowerCase().includes(search) ||
              (row.indication || '').toLowerCase().includes(search)
            );
          } catch (error) {
            console.error('Search filter error for row:', row, error);
            return false;
          }
        });
      }

      // Sort
      filtered.sort((a, b) => {
        try {
          let aVal = a[sortField];
          let bVal = b[sortField];

          // Handle null/undefined values
          if (aVal == null) aVal = '';
          if (bVal == null) bVal = '';

          if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = String(bVal).toLowerCase();
            return sortDirection === 'asc' ? aVal.localeCompare(bVal, 'tr') : bVal.localeCompare(aVal, 'tr');
          }
          if (typeof aVal === 'number') {
            const numA = Number(aVal) || 0;
            const numB = Number(bVal) || 0;
            return sortDirection === 'asc' ? numA - numB : numB - numA;
          }
          return 0;
        } catch (error) {
          console.error('Sort error:', error, 'Field:', sortField, 'Values:', a[sortField], b[sortField]);
          return 0;
        }
      });

      return filtered;
    } catch (error) {
      console.error('Filter and sort error:', error);
      toast.error(label('toast.filterError'));
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRows, searchTerm, sortField, sortDirection, roleFilter, statusFilter]);

  // Kişiye ait bir sütuna göre sıralandığında satırlar SC bazında gruplanır.
  const isGrouped = PERSON_LEVEL_FIELDS.includes(sortField);

  // Aynı SC'nin satırlarını tek blokta toplar. Map ekleme sırasını koruduğu
  // için gruplar sıralamadaki sırayı izler; aynı adı taşıyan iki farklı kişi
  // employeeId sayesinde ayrı bloklarda kalır.
  const groupedRows = useMemo(() => {
    if (!isGrouped) return [];
    const groups = new Map();
    filteredAndSortedRows.forEach((row) => {
      const key = row.employeeId || `name:${row.scName}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return [...groups.values()];
  }, [filteredAndSortedRows, isGrouped]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const uniqueSCs = new Set(filteredAndSortedRows.map(r => r.employeeId));
    const uniqueProjects = new Set(filteredAndSortedRows.map(r => r.projectId));
    const uniqueSites = new Set(filteredAndSortedRows.filter(r => r.site).map(r => r.site));
    return {
      totalRows: filteredAndSortedRows.length,
      uniqueSCs: uniqueSCs.size,
      uniqueProjects: uniqueProjects.size,
      uniqueSites: uniqueSites.size,
      totalScreened: filteredAndSortedRows.reduce((sum, r) => sum + (r.screenedPatients || 0), 0),
      totalRandomized: filteredAndSortedRows.reduce((sum, r) => sum + (r.randomization || 0), 0),
      totalActive: filteredAndSortedRows.reduce((sum, r) => sum + (r.currentPatients || 0), 0)
    };
  }, [filteredAndSortedRows]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Excel export
  const handleExcelExport = () => {
    const exportData = filteredAndSortedRows.map(row => ({
      'SC': row.scName,
      'Durum': row.employeeStatus === 'active' ? 'Aktif' : 'Pasif',
      'Telefon': row.phone,
      'E-Posta': row.email,
      'Hizmet-FTE': row.serviceFte,
      'Proje': row.project,
      'Protokol': row.protocolNumber,
      'Endikasyon': row.indication,
      'Merkez': row.site,
      'Merkezden Ayrılma': row.isArchived ? formatUnassignedDate(row) : '',
      'PI': row.pi,
      'Delegasyon': row.delegation,
      'Hedeflenen Hasta': row.targetedPatients,
      'Screen Edilen': row.screenedPatients,
      'Screen Failure': row.screenFailure,
      'Randomizasyon': row.randomization,
      'Güncel Hasta': row.currentPatients,
      'Sponsor': row.sponsor
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SC Takip');
    XLSX.writeFile(wb, `SC_Takip_Tablosu_${new Date().toLocaleDateString('tr-TR')}.xlsx`);
    toast.success(label('toast.exported'));
  };

  const columns = [
    { key: 'scName', width: 'tw-min-w-[12rem]' },
    { key: 'employeeStatus', width: 'tw-min-w-[5.5rem]' },
    { key: 'phone', width: 'tw-min-w-[8rem]' },
    { key: 'email', width: 'tw-min-w-[12rem]' },
    { key: 'serviceFte', width: 'tw-min-w-[6.5rem]' },
    { key: 'project', width: 'tw-min-w-[9rem]' },
    { key: 'protocolNumber', width: 'tw-min-w-[8rem]' },
    { key: 'indication', width: 'tw-min-w-[8rem]' },
    { key: 'site', width: 'tw-min-w-[13rem]' },
    { key: 'pi', width: 'tw-min-w-[11rem]' },
    { key: 'delegation', width: 'tw-min-w-[7rem]' },
    { key: 'targetedPatients', width: 'tw-min-w-[6rem]', numeric: true },
    { key: 'screenedPatients', width: 'tw-min-w-[6rem]', numeric: true },
    { key: 'screenFailure', width: 'tw-min-w-[6rem]', numeric: true },
    { key: 'randomization', width: 'tw-min-w-[6.5rem]', numeric: true },
    { key: 'currentPatients', width: 'tw-min-w-[6rem]', numeric: true },
    { key: 'sponsor', width: 'tw-min-w-[9rem]' }
  ];

  const ariaSort = (field) => {
    if (sortField !== field) return 'none';
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  const summaryTiles = [
    { key: 'scPeople', value: summaryStats.uniqueSCs, icon: LuUsers, tone: 'tw-bg-sky-500/10 tw-text-sky-600 dark:tw-text-sky-300' },
    { key: 'projects', value: summaryStats.uniqueProjects, icon: LuFolderKanban, tone: 'tw-bg-violet-500/10 tw-text-violet-600 dark:tw-text-violet-300' },
    { key: 'sites', value: summaryStats.uniqueSites, icon: LuBuilding2, tone: 'tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300' },
    { key: 'activePatients', value: summaryStats.totalActive, icon: LuStethoscope, tone: 'tw-bg-amber-500/10 tw-text-amber-600 dark:tw-text-amber-300' },
  ];

  const rowTone = (row) => {
    if (row.employeeStatus === 'inactive') return 'tw-opacity-60';
    if (row.role === 'Backup Personeli') return 'tw-bg-amber-500/[0.04]';
    return '';
  };

  return (
    <CollapsibleCard
      icon={LuSheet}
      gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30"
      title={label('title')}
      description={label('description')}
      badge={dataLoaded ? <Badge tone="accent">{label('badge', { sc: summaryStats.uniqueSCs, projects: summaryStats.uniqueProjects })}</Badge> : null}
      open={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
    >
      {isLoading ? (
        <LoadingRows count={5} height="tw-h-12" label={label('loading')} />
      ) : (
        <>
          {/* Özet */}
          <dl className="tw-grid tw-grid-cols-2 tw-gap-2 lg:tw-grid-cols-4">
            {summaryTiles.map((tile) => {
              const Icon = tile.icon;
              return (
                <div key={tile.key} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                  <span className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl ${tile.tone}`}>
                    <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </span>
                  <div className="tw-min-w-0">
                    <dd className="tw-text-xl tw-font-semibold tw-leading-none tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{tile.value}</dd>
                    <dt className="tw-mt-1 tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label(`summary.${tile.key}`)}</dt>
                  </div>
                </div>
              );
            })}
          </dl>

          {/* Araç çubuğu */}
          <div className="tw-flex tw-flex-col tw-gap-2 lg:tw-flex-row lg:tw-items-center">
            <SearchField value={searchTerm} onChange={(value) => handleSearchChange({ target: { value } })} placeholder={label('search')} className="lg:tw-max-w-sm lg:tw-flex-1" />
            <div className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-flex sm:tw-items-center">
              <span className="tw-block sm:tw-w-48">
                <SelectInput
                  aria-label={label('roleFilter')}
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  options={['all', 'sc', 'backup', 'nurse', 'scSorumlusu'].map((value) => ({ value, label: label(`roles.${value}`) }))}
                />
              </span>
              <span className="tw-block sm:tw-w-40">
                <SelectInput
                  aria-label={label('statusFilter')}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  options={['all', 'active', 'inactive'].map((value) => ({ value, label: label(`statuses.${value}`) }))}
                />
              </span>
            </div>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 lg:tw-ml-auto">
              <Button size="sm" variant="ghost" onClick={handleRefresh} disabled={isLoading} title={label('refreshHint')}>
                <LuRefreshCw aria-hidden="true" className={`tw-h-4 tw-w-4 ${isLoading ? 'tw-animate-spin' : ''}`} />
                {label('refresh')}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleClearFilters} title={label('clearHint')}>
                <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
                {label('clear')}
              </Button>
              <Button size="sm" onClick={handleExcelExport}>
                <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
                {label('excel')}
              </Button>
            </div>
          </div>

          {/* Tablo */}
          <div className="tw-max-h-[70vh] tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200/80 glass-scrollbar dark:tw-border-white/10">
            <table className="tw-w-full tw-border-collapse tw-text-left">
              <thead className="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50 dark:tw-bg-[#2a2e37]">
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} scope="col" aria-sort={ariaSort(col.key)} className={`tw-p-0 ${col.width}`}>
                      <button
                        type="button"
                        onClick={() => handleSort(col.key)}
                        className={`tw-flex tw-w-full tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 hover:tw-text-slate-800 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-teal-500/50 dark:tw-text-slate-400 dark:hover:tw-text-white ${col.numeric ? 'tw-justify-end' : ''}`}
                      >
                        {label(`columns.${col.key}`)}
                        {sortField !== col.key && <LuArrowUpDown aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-opacity-40" />}
                        {sortField === col.key && sortDirection === 'asc' && <LuArrowUp aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-teal-600 dark:tw-text-teal-300" />}
                        {sortField === col.key && sortDirection === 'desc' && <LuArrowDown aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-teal-600 dark:tw-text-teal-300" />}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-slate-200/70 dark:tw-divide-white/[0.06]">
                {filteredAndSortedRows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="tw-p-0">
                      <EmptyState compact icon={LuSheet} title={label('empty')} />
                    </td>
                  </tr>
                ) : isGrouped ? (
                  // Gruplu görünüm: kişi sütunları grubun tamamına yayılan
                  // tek hücrede; gruplar arası çizgi daha belirgin.
                  groupedRows.map((group, groupIndex) =>
                    group.map((row, rowIndex) => (
                      <tr
                        key={`g${groupIndex}_${rowIndex}_${row.projectId}_${row.centerId}`}
                        className={`${rowIndex === 0 && groupIndex > 0 ? 'tw-border-t-2 tw-border-t-slate-300 dark:tw-border-t-white/15' : ''} ${rowTone(row)}`}
                      >
                        {rowIndex === 0 && (
                          <IdentityCells
                            row={row}
                            rowSpan={group.length}
                            groupSize={group.length}
                          />
                        )}
                        <AssignmentCells row={row} />
                      </tr>
                    ))
                  )
                ) : (
                  filteredAndSortedRows.map((row, index) => (
                    <tr
                      key={`${row.employeeId}_${row.projectId}_${row.centerId}_${index}`}
                      className={`${index % 2 === 0 ? '' : 'tw-bg-slate-50/50 dark:tw-bg-white/[0.015]'} ${rowTone(row)}`}
                    >
                      <IdentityCells row={row} />
                      <AssignmentCells row={row} />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Alt bilgi */}
          <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            {label('footer.count', { count: filteredAndSortedRows.length })}
            {searchTerm && ` ${label('footer.filteredFrom', { total: tableRows.length })}`}
            {isGrouped && filteredAndSortedRows.length > 0 && (
              <span className="tw-text-teal-700 dark:tw-text-teal-300">
                {' • '}
                {label('footer.grouped', { count: groupedRows.length })}
              </span>
            )}
            {(roleFilter !== 'all' || statusFilter !== 'all') && (
              <span className="tw-text-amber-700 dark:tw-text-amber-300">
                {' • '}
                {roleFilter !== 'all' && `${label('roleFilter')}: ${label(`roles.${roleFilter}`)}`}
                {roleFilter !== 'all' && statusFilter !== 'all' && ', '}
                {statusFilter !== 'all' && `${label('statusFilter')}: ${label(`statuses.${statusFilter}`)}`}
              </span>
            )}
          </p>
        </>
      )}
    </CollapsibleCard>
  );
};

export default SCTrackingTable;
